import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Globe from 'react-globe.gl';

/** 主要枢纽（涟漪环） */
const RING_HUBS = [
  { lat: 36.07, lng: 120.38 },
  { lat: 31.23, lng: 121.47 },
  { lat: 51.92, lng: 4.48 },
  { lat: 1.35, lng: 103.82 },
  { lat: 25.2, lng: 55.27 },
  { lat: 34.05, lng: -118.25 },
];

/** 跨洋/干线弧线（夜间地球 + 橙弧，参考航线可视化） */
const ROUTE_ARCS = [
  { startLat: 36.07, startLng: 120.38, endLat: 34.05, endLng: -118.25 },
  { startLat: 31.23, startLng: 121.47, endLat: 51.92, endLng: 4.48 },
  { startLat: 31.23, startLng: 121.47, endLat: 1.35, endLng: 103.82 },
  { startLat: 31.23, startLng: 121.47, endLat: 25.2, endLng: 55.27 },
  { startLat: 51.92, startLng: 4.48, endLat: 40.71, endLng: -74.01 },
  { startLat: 51.92, startLng: 4.48, endLat: 53.55, endLng: 9.99 },
  { startLat: 1.35, startLng: 103.82, endLat: -33.87, endLng: 151.2 },
  { startLat: 25.2, startLng: 55.27, endLat: 31.23, endLng: 121.47 },
];

const GLOBE_TEXTURES = {
  globe: 'https://unpkg.com/three-globe/example/img/earth-night.jpg',
  bump: 'https://unpkg.com/three-globe/example/img/earth-topology.png',
  sky: 'https://unpkg.com/three-globe/example/img/night-sky.png',
};

export default function TowerGlobe() {
  const globeRef = useRef();
  const hostRef = useRef(null);
  const [dims, setDims] = useState({ w: 640, h: 380 });

  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0].contentRect;
      const w = Math.max(280, Math.floor(cr.width));
      const h = Math.max(300, Math.floor(cr.height));
      setDims({ w, h });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const arcsData = useMemo(() => ROUTE_ARCS, []);
  const ringsData = useMemo(() => RING_HUBS, []);

  const onGlobeReady = useCallback(() => {
    const g = globeRef.current;
    if (!g) return;
    const ctrl = g.controls();
    ctrl.autoRotate = true;
    ctrl.autoRotateSpeed = 0.42;
    ctrl.enableDamping = true;
    g.pointOfView({ lat: 22, lng: 118, altitude: 2.18 }, 0);
  }, []);

  return (
    <div ref={hostRef} className="tower-globe-host">
      <Globe
        ref={globeRef}
        width={dims.w}
        height={dims.h}
        waitForGlobeReady
        globeImageUrl={GLOBE_TEXTURES.globe}
        bumpImageUrl={GLOBE_TEXTURES.bump}
        backgroundImageUrl={GLOBE_TEXTURES.sky}
        backgroundColor="rgba(2,6,23,0.96)"
        showGraticules={false}
        atmosphereColor="rgba(56, 189, 248, 0.32)"
        atmosphereAltitude={0.18}
        onGlobeReady={onGlobeReady}
        arcsData={arcsData}
        arcColor={() => ['#fdba74', '#ea580c']}
        arcAltitude={0.25}
        arcStroke={0.25}
        arcDashLength={0.36}
        arcDashGap={2.2}
        arcDashInitialGap={() => Math.random()}
        arcDashAnimateTime={() => 2800 + Math.random() * 1600}
        arcsTransitionDuration={1800}
        ringsData={ringsData}
        ringColor={() => 'rgba(251, 146, 60, 0.55)'}
        ringMaxRadius={5}
        ringPropagationSpeed={2.5}
        ringRepeatPeriod={750}
      />
    </div>
  );
}
